import { useMemo, useState } from "react";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SELLER_PROFILE_STATUS_LABELS } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { Badge, Button, Textarea, statusColor, useConfirm } from "../../components/ui";
import { DataTable, type Column } from "../../components/DataTable";
import { RightSidebarPanel } from "../../components/RightSidebarPanel";
import { flattenInfinitePages, getNextPageParam } from "../../lib/infinite-list";
import { fmtDate } from "../../lib/format";
import { showQueryLoader } from "../../lib/query-utils";

interface SellerRow {
  id: string;
  shopName: string;
  shopSlug: string;
  status: string;
  updatedAt: string;
  user: { email: string; displayName: string | null };
}

const PAGE_SIZE = 25;

const VIEW_OPTIONS = [
  { value: "KYC_SUBMITTED", label: "Pending submission" },
  { value: "KYC_REJECTED", label: "Rejected" },
  { value: "ALL", label: "All" },
];

function DocLink({ href, label }: { href: string | null | undefined; label: string }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between gap-2 rounded-xl border border-line bg-surface-2/50 px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-hover"
    >
      <span>{label}</span>
      <ExternalLink size={14} className="shrink-0 text-muted" />
    </a>
  );
}

export function SellerKycVerificationPanel() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [view, setView] = useState("KYC_SUBMITTED");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [reverifyNote, setReverifyNote] = useState("");

  const listQuery = useInfiniteQuery({
    queryKey: ["marketplace-sellers-kyc", view, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (view !== "ALL") params.set("status", view);
      if (search.trim()) params.set("q", search.trim());
      return api<{ items: SellerRow[]; total: number }>(`/marketplace/admin/sellers?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.items.length, 0), PAGE_SIZE),
  });

  const detailQuery = useQuery({
    queryKey: ["marketplace-seller", selectedId],
    queryFn: () =>
      api<{
        seller: SellerRow & { user: { phone?: string } };
        kycDocs: Record<string, string | null>;
        bankMasked: string | null;
        tinMasked: string | null;
        correspondenceAddress: {
          line1: string | null;
          line2: string | null;
          city: string | null;
          postcode: string | null;
          state: string | null;
          country: string | null;
        } | null;
      }>(`/marketplace/admin/sellers/${selectedId}`),
    enabled: !!selectedId,
  });

  const reviewMut = useMutation({
    mutationFn: (approve: boolean) =>
      api(`/marketplace/admin/sellers/${selectedId}/kyc/review`, {
        method: "POST",
        body: { approve, reviewNote: reviewNote || undefined },
      }),
    onSuccess: (_, approve) => {
      toast("success", approve ? "Information approved" : "Information rejected");
      setReviewNote("");
      void qc.invalidateQueries({ queryKey: ["marketplace-sellers-kyc"] });
      void qc.invalidateQueries({ queryKey: ["marketplace-seller", selectedId] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Review failed"),
  });

  const reverifyMut = useMutation({
    mutationFn: () =>
      api(`/marketplace/admin/sellers/${selectedId}/request-reverification`, {
        method: "POST",
        body: { note: reverifyNote.trim() || undefined },
      }),
    onSuccess: () => {
      toast("success", "Re-verification requested");
      setReverifyNote("");
      void qc.invalidateQueries({ queryKey: ["marketplace-sellers-kyc"] });
      void qc.invalidateQueries({ queryKey: ["marketplace-seller", selectedId] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Request failed"),
  });

  const columns = useMemo<Column<SellerRow>[]>(
    () => [
      {
        key: "shop",
        header: "Shop",
        sortable: true,
        sortValue: (r) => r.shopName,
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{r.shopName}</p>
            <p className="font-mono text-[10px] text-muted">{r.shopSlug}</p>
          </div>
        ),
      },
      {
        key: "seller",
        header: "Account",
        hideSm: true,
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate text-xs text-foreground">{r.user.displayName ?? r.user.email}</p>
            <p className="truncate text-[10px] text-muted">{r.user.email}</p>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortValue: (r) => r.status,
        render: (r) => (
          <Badge color={statusColor(r.status === "ACTIVE" ? "ACTIVE" : "PENDING")}>
            {SELLER_PROFILE_STATUS_LABELS[r.status] ?? r.status}
          </Badge>
        ),
      },
      {
        key: "updated",
        header: "Updated",
        hideSm: true,
        sortValue: (r) => r.updatedAt,
        render: (r) => <span className="text-xs text-muted">{fmtDate(r.updatedAt)}</span>,
      },
    ],
    [],
  );

  const rows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = rows.length < listTotal && (listQuery.hasNextPage ?? false);
  const detail = detailQuery.data;
  const seller = detail?.seller;

  return (
    <>
      {dialog}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={showQueryLoader(listQuery)}
        entityLabel="Seller"
        fillViewport
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        onRowClick={(r) => setSelectedId(r.id)}
        activeKey={selectedId ?? undefined}
        toolbar={{
          search: {
            value: search,
            onChange: setSearch,
            onSubmit: () => void listQuery.refetch(),
            placeholder: "Search shop or email",
          },
          views: {
            options: VIEW_OPTIONS,
            value: view,
            onChange: setView,
          },
          onRefresh: () => void listQuery.refetch(),
        }}
        empty={{
          icon: ShieldCheck,
          title: "No seller verifications",
          hint:
            view === "KYC_SUBMITTED"
              ? "Pending KYC submissions will appear here."
              : "Try another filter or search.",
        }}
      />

      <RightSidebarPanel
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={seller?.shopName ?? "Seller KYC"}
        footer={
          seller ? (
            <div className="flex w-full flex-wrap justify-end gap-2">
              {seller.status === "KYC_SUBMITTED" ? (
                <>
                  <Button variant="outline" disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(false)}>
                    Reject
                  </Button>
                  <Button variant="primary" disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(true)}>
                    Approve
                  </Button>
                </>
              ) : seller.status === "ACTIVE" ? (
                <Button
                  variant="outline"
                  disabled={reverifyMut.isPending}
                  onClick={() =>
                    confirm({
                      title: "Request re-verification",
                      message:
                        "The seller must complete the information wizard again before staff can approve. Their shop may be limited until approved.",
                      confirmLabel: "Request",
                      action: () => reverifyMut.mutate(),
                    })
                  }
                >
                  Request re-verification
                </Button>
              ) : null}
            </div>
          ) : null
        }
      >
        {detail && seller && (
          <div className="space-y-6 text-sm">
            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Account</p>
              <p className="text-foreground">{seller.user.email}</p>
              {seller.user.displayName ? <p className="text-muted">{seller.user.displayName}</p> : null}
              <Badge color={statusColor(seller.status === "ACTIVE" ? "active" : "pending")}>
                {SELLER_PROFILE_STATUS_LABELS[seller.status] ?? seller.status}
              </Badge>
            </section>

            {(detail.kycDocs.idFrontUrl || detail.bankMasked) && (
              <section className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Verification documents</p>
                <div className="grid gap-2">
                  <DocLink href={detail.kycDocs.idFrontUrl} label="MyKad / NRIC" />
                  <DocLink href={detail.kycDocs.bankStatementUrl} label="Bank statement" />
                  <DocLink href={detail.kycDocs.proofOfAddressUrl} label="Proof of address" />
                  <DocLink href={detail.kycDocs.selfieUrl} label="Selfie with ID" />
                </div>
                {detail.bankMasked ? <p className="text-xs text-muted">Bank account · {detail.bankMasked}</p> : null}
                {detail.tinMasked ? <p className="text-xs text-muted">TIN · {detail.tinMasked}</p> : null}
                {detail.correspondenceAddress?.line1 ? (
                  <p className="text-xs text-muted">
                    {detail.correspondenceAddress.line1}
                    {detail.correspondenceAddress.line2 ? `, ${detail.correspondenceAddress.line2}` : ""}
                    <br />
                    {detail.correspondenceAddress.postcode} {detail.correspondenceAddress.city},{" "}
                    {detail.correspondenceAddress.state}
                  </p>
                ) : null}
              </section>
            )}

            {seller.status === "KYC_SUBMITTED" && (
              <section className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Review</p>
                <Textarea
                  label="Review note"
                  hint="Shown to the seller if you reject"
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                />
              </section>
            )}

            {seller.status === "ACTIVE" && (
              <section className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Re-verification</p>
                <Textarea
                  label="Message to seller (optional)"
                  hint="Included in the seller portal and notification email"
                  value={reverifyNote}
                  onChange={(e) => setReverifyNote(e.target.value)}
                  rows={3}
                />
              </section>
            )}
          </div>
        )}
      </RightSidebarPanel>
    </>
  );
}
